import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import ClarityHeroField from '../ClarityHeroField';
import { EducationDesignPreview } from '../education/EducationDesignExperiment';
import CoursePage from './CoursePage';
import '../education/EducationControlRoom.css';
import './CoursePageControlRoom.css';
import './CoursePageClarity.css';
import '../challenges/ChallengePageClarity.css';

export const COURSE_CLARITY_REVIEW_SLUG = 'ai-trading-bootcamp';

function CourseClarityField(): React.ReactElement | null {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const root = document.querySelector('[data-testid="course-clarity-review"]');
    const find = () => {
      const next = root?.querySelector('.cpw-mast');
      return next instanceof HTMLElement ? next : null;
    };

    const found = find();
    if (found) {
      setHost(found);
      return undefined;
    }

    const observer = new MutationObserver(() => {
      const next = find();
      if (next) {
        setHost(next);
        observer.disconnect();
      }
    });
    if (root) observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  if (!host) return null;

  return createPortal(
    <ClarityHeroField
      className="course-clarity-review__field"
      copyKeep="columns"
      hostSelector=".cpw-mast"
    />,
    host,
  );
}

/**
 * Review route: /courses-clarity and /courses-clarity/ai-trading-bootcamp.
 * Mounts the live PostHog test (Clarity) CoursePage without enrollment.
 * Does not evaluate ai_trading_bootcamp_control_vs_clarity_2026_08_31.
 */
export default function CourseClarityReview(): React.ReactElement {
  const { slug: paramSlug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const pathSlug = location.pathname.match(/^\/courses-clarity\/([^/]+)/)?.[1];
  const slug = paramSlug || pathSlug;
  const resolvedSlug = slug || COURSE_CLARITY_REVIEW_SLUG;

  if (!slug) {
    return (
      <Navigate to={`/courses-clarity/${resolvedSlug}${location.search}${location.hash}`} replace />
    );
  }

  return (
    <div
      className="course-clarity-review education-design-experiment education-design-experiment--control-room education-design-experiment--clarity"
      data-testid="course-clarity-review"
      data-education-design-variant="clarity"
      data-education-theme="dark"
      data-education-theme-source="review"
      data-experiment-active="false"
    >
      <EducationDesignPreview variant="clarity">
        <CoursePage />
      </EducationDesignPreview>
      <CourseClarityField />
    </div>
  );
}
